import React, { useEffect, useCallback, useState } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Stack,
    Grid,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Card,
    styled,
    ListItemText,
    ListItemButton,
    Collapse,
    List,
    ListItem,
    Typography,
    Chip,
    Button,
    InputAdornment
} from '@mui/material';

import { useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';

import { useNavigate, useParams } from "react-router-dom";
import CommonBreadcumbs from '../common/CommonBreadcumbs';
import CommonSectionHeader from "../common/CommonSectionHeader";
import CommonRestricted from '../common/CommonRestricted';
import { CommonIcons, icons } from "../common/CommonIcons";

import { useAuthAccess } from "../../hooks/useAuthAccess";
import { useRates, rateTypes, targetTypes as rateTargetTypes } from '../../hooks/api/useRates';

import { useSystemPrompt } from "../../hooks/useSystemPrompt";
import { useAppContextFeatures } from '../../context/AppContext';
import { useSystemRoutes, routeTypes } from '../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../hooks/usePermissions';
import { useTranslations } from '../../hooks/useTranslations';

import { initialValues, validationSchema } from "../../schema/tax/taxRateSchema";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function UpdateTaxRate({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@tax"] })

    const { id } = useParams();

    const { promptMessage } = useSystemPrompt();
    const { isLoading, get, update } = useRates({ rateType: rateTypes.taxRate, targetType: rateTargetTypes.taxRate });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.rate.id, scopeTypes.rate.taxRate.id)

    const { setSystemLoading } = useAppContextFeatures();

    const navigate = useNavigate();

    const [savedValues, setSavedValues] = useState()
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const loadData = useCallback(async (id) => {

        setSystemLoading(true)

        try {

            const { data } = await get({ id })
            const { rate } = data;

            setSavedValues({
                rateName: rate.rate_name,
                locationId: rate.scope_id,
                description: rate.description,
                status: rate?.status === "1",
                taxPercentage: parseFloat(rate?.tax_percentage).toFixed(2)
            })

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }, [id])

    useEffect(() => {

        loadData(id);

    }, [])

    const onSubmit = async (currentValues) => {

        try {

            let data = {
                rate: {
                    rate_name: currentValues?.rateName,
                    description: currentValues?.description,
                    status: currentValues?.status ? 1 : 0,
                },
                props: {
                    tax_percentage: parseFloat(currentValues?.taxPercentage).toFixed(2)
                }
            }
            const response = await update({ id, data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("settings/taxRates"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }
    }

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const RenderMainInfo = () => {

        return (
            <>
                <StyledCard>
                    <Grid container spacing={0.5}>

                        <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                            <CommonSectionHeader
                                title={t('rateInfo')}
                                subTitle={t('detailsOfTaxRateCharges')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={9}>
                            <Grid container spacing={1}>

                                <Grid item xs={12} sm={12} md={6}>
                                    <FormControlLabel
                                        sx={{
                                            marginLeft: '1px'
                                        }}
                                        control={
                                            <Switch
                                                sx={{
                                                    mt: 1
                                                }}
                                                name="status"
                                                checked={Boolean(values?.status)}
                                                onChange={(event, checked) => {
                                                    setFieldValue("status", checked);
                                                }}
                                            />
                                        }
                                        label={
                                            Boolean(values?.status) ? t("enabled") : t("disabled")
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={6}>
                                    <></>
                                </Grid>

                                <Grid item xs={12} sm={6} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"rateName"}
                                        label={t("rateName")}
                                        type="text"
                                        variant="outlined"
                                        value={values?.rateName}
                                        onChange={handleChange}
                                        error={touched.rateName && Boolean(errors.rateName)}
                                        helperText={touched.rateName && errors.rateName}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"taxPercentage"}
                                        label={t("taxPercentage")}
                                        type="text"
                                        variant="outlined"
                                        value={values?.taxPercentage}
                                        onChange={handleChange}
                                        error={touched.taxPercentage && Boolean(errors.taxPercentage)}
                                        helperText={touched.taxPercentage && errors.taxPercentage}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <CommonIcons name={icons.percentage} />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"description"}
                                        label={t("description")}
                                        type="text"
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        value={values?.description}
                                        onChange={handleChange}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description}
                                    />
                                </Grid>

                            </Grid>
                        </Grid>
                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (
            <form onSubmit={formik.handleSubmit}>
                <Grid>
                    {
                        RenderMainInfo()
                    }
                </Grid>
            </form>
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.updateTaxRate })
            }

            <CommonBreadcumbs
                pageTitle={t("updateTaxRate")}
                breadcrumbs={[
                    {
                        displayText: t("taxRates"),
                        props: {
                            href: getRoute("settings/taxRates")
                        }
                    },
                    {
                        displayText: t("updateTaxRate"),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}